import * as React from "react";
import classnames from "classnames";
import { animated, config, useTransition } from "@react-spring/web";
import { Button, makeStyles } from "@material-ui/core";
import { MoreVert } from "@material-ui/icons";
import ReactTooltip from "react-tooltip";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";
import { getFormattedZonedTimestamp } from "@util/date.util";
import { greys } from "@styles/color.styles";
import { fontFamilySans } from "@styles/type.styles";

export enum TimelineLogType {
  PastLog = "PastLog",
  FutureLog = "FutureLog",
  Action = "Action",
  Info = "Info",
}
export interface TimelineLog {
  icon: React.ReactNode | null;
  description: React.ReactNode;
  defragInfo: React.ReactNode;
  timestamp: number | null;
  type: TimelineLogType;
}

export interface TimelineRowProps {
  icon: React.ReactNode;
  description: React.ReactNode;
  defragInfo: React.ReactNode;
  timestamp: number | null;
  type: TimelineLogType;
  index: number;
  numLogs: number;
  timelineCollapsed: boolean;
  isMissingHistory?: boolean;
  onClickShowMoreHistory: () => void;
}
export interface TimelineProps {
  timelineLogs: TimelineLog[];
  isMissingHistory?: boolean;
  trackTimelineExpanded: (payload: Record<string, unknown>) => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  timelineRow: {
    display: "flex",
    gap: 12,
  },
  iconWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 0,
    minWidth: 16,

    // icon
    "& > svg": {
      color: greys.blueGrey,
      width: 16,
      height: 16,
    },
  },
  iconWrapper__autopilotStatus: {
    marginTop: 12,
  },
  timelineSeparator: {
    border: `solid 1px ${greys.blueGrey}`,
    flex: 1,
    margin: 4,
    minHeight: 10,
  },
  timelineSeparator__dashed: {
    border: `dashed 1px ${greys.blueGrey}`,
  },
  description: {
    margin: "0px 0px 4px",
    fontFamily: fontFamilySans,
    lineHeight: 1.28,
    fontSize: `${13 / 16}rem`,
    color: greys.darkGreyBlue,
    padding: 0,
    textTransform: "none",
    fontWeight: 400,
  },
  info: {
    color: greys.blueGrey,
    marginBottom: 14,
  },
  timestamp: {
    fontFamily: fontFamilySans,
    lineHeight: 1.28,
    fontSize: `${12 / 16}rem`,
    color: greys.blueGrey,
    margin: "0px 0px 14px",
    cursor: "pointer",
  },
  action: {
    margin: "0px 0px 4px",
    fontFamily: fontFamilySans,
    lineHeight: 1.28,
    fontSize: `${13 / 16}rem`,
    color: greys.blueGrey,
    padding: 0,
    textTransform: "none",
    fontWeight: 400,

    "&:hover": {
      backgroundColor: "transparent",
      color: greys[2],
      transition: "color 250ms",
    },
  },
});

const getTimestampLabel = (timestamp: number) => {
  return new ZonedMoment(timestamp).fromNow();
};

const INITIAL_HISTORY_VIEW_LOG_LIMIT = 5;

const TimelineRow = ({
  icon,
  description,
  defragInfo,
  type,
  timestamp,
  index,
  numLogs,
  timelineCollapsed,
  isMissingHistory,
  onClickShowMoreHistory,
}: TimelineRowProps) => {
  const classes = useStyles();

  // the vertical lines connecting timeline icons
  const renderTimelineSeparator = () => {
    const isDisconnectedTimelineLog = timelineCollapsed
      ? index >= numLogs - 1
      : index >= numLogs - 2;

    // if we are missing history and in the expanded timeline view,
    // the second to last log represents a break in the timeline
    // so we don't render a separator after the last *three* logs.
    const isDisconnectedMissingHistoryLog =
      isMissingHistory && !timelineCollapsed && index >= numLogs - 3;

    if (isDisconnectedTimelineLog || isDisconnectedMissingHistoryLog) {
      return null;
    }

    return (
      <div
        className={classnames(classes.timelineSeparator, {
          [classes.timelineSeparator__dashed]: type === TimelineLogType.FutureLog,
        })}
      ></div>
    );
  };

  return (
    <div className={classes.timelineRow} cw-id="history-timeline-event">
      <div
        className={classnames(classes.iconWrapper, {
          [classes.iconWrapper__autopilotStatus]: type === TimelineLogType.FutureLog,
        })}
      >
        {icon}
        {renderTimelineSeparator()}
      </div>
      <div>
        {type === TimelineLogType.Action ? (
          <Button variant="text" className={classes.action} onClick={onClickShowMoreHistory}>
            {description}
          </Button>
        ) : (
          <div
            className={classnames(classes.description, {
              [classes.info]: type === TimelineLogType.Info,
            })}
          >
            {description}
            {defragInfo}
          </div>
        )}
        {timestamp && (
          <div
            data-for={`${timestamp}-${index}`}
            data-tip=""
            /* Needed to render empty tooltip */
            className={classes.timestamp}
          >
            {getTimestampLabel(timestamp)}
            <ReactTooltip multiline type="dark" id={`${timestamp}-${index}`}>
              {getFormattedZonedTimestamp(timestamp)}
            </ReactTooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export const Timeline = ({
  timelineLogs,
  isMissingHistory,
  trackTimelineExpanded,
}: TimelineProps) => {
  const classes = useStyles();

  // always collapse history if we're over the initial log limit
  const [timelineCollapsed, setTimedlineCollapsed] = React.useState(
    timelineLogs.length - 1 > INITIAL_HISTORY_VIEW_LOG_LIMIT,
  );

  const augmentTimelineLogs = () => {
    // the BE supplies logs in chronological order,
    // but we want to render them reverse-chronologically
    const orderedLogs = [...timelineLogs].reverse();

    let displayLogs: TimelineLog[];

    // if we're not collapsing the view, or we have
    // fewer log entries than our limit return every log
    if (!timelineCollapsed) {
      displayLogs = [...orderedLogs];
      if (isMissingHistory) {
        const missingHistoryInfoLog: TimelineLog = {
          icon: null,
          description: `Clockwise doesn't keep history older than 14 days`,
          defragInfo: null,
          timestamp: null,
          type: TimelineLogType.Info,
        };

        // if we're missing history, and showing an expanded timeline
        // add an info log in the second to last position
        // where orderedLogs.length -1 is the index at which to start
        // changing the array
        displayLogs.splice(orderedLogs.length - 1, 0, missingHistoryInfoLog);
      }
    } else {
      // if we're collapsing history,
      // limit the number of initial logs displayed
      // and insert a button in the second to last log position
      const showMoreHistoryActionLog: TimelineLog = {
        icon: <MoreVert />,
        description: "Show more history",
        defragInfo: null,
        timestamp: null,
        type: TimelineLogType.Action,
      };

      displayLogs = [
        ...orderedLogs.slice(0, INITIAL_HISTORY_VIEW_LOG_LIMIT),
        showMoreHistoryActionLog,
        orderedLogs[orderedLogs.length - 1],
      ];
    }

    return displayLogs;
  };

  const [augmentedTimelineLogs, setAugmentedTimelineLogs] = React.useState<TimelineLog[]>(
    augmentTimelineLogs(),
  );

  React.useEffect(() => {
    setTimedlineCollapsed(timelineLogs.length - 1 > INITIAL_HISTORY_VIEW_LOG_LIMIT);
  }, [timelineLogs]);

  React.useEffect(() => {
    const updatedLogs = augmentTimelineLogs();
    setAugmentedTimelineLogs(updatedLogs);
  }, [timelineLogs, isMissingHistory, timelineCollapsed]);

  const transitions = useTransition(augmentedTimelineLogs, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    trail: 25,
    config: config.molasses,
  });

  const onClickShowMoreHistory = () => {
    setTimedlineCollapsed(false);
    trackTimelineExpanded({
      numItemsTotal: timelineLogs.length,
    });
  };

  return (
    <div className={classes.container}>
      {transitions(({ opacity }, { type, description, defragInfo, icon, timestamp }, _, index) => (
        <animated.div
          key={index}
          style={{
            // only transition "collapsed" events
            opacity:
              index > INITIAL_HISTORY_VIEW_LOG_LIMIT && !timelineCollapsed
                ? opacity.to({ range: [0.0, 1.0], output: [0, 1] })
                : 1,
          }}
        >
          <TimelineRow
            icon={icon}
            description={description}
            defragInfo={defragInfo}
            type={type}
            timestamp={timestamp}
            index={index}
            numLogs={augmentedTimelineLogs.length}
            timelineCollapsed={timelineCollapsed}
            isMissingHistory={isMissingHistory}
            onClickShowMoreHistory={onClickShowMoreHistory}
          />
        </animated.div>
      ))}
    </div>
  );
};
